import React, { memo, useCallback, useMemo } from 'react';
import { Badge, Button, Pagination, Spinner, Table } from 'react-bootstrap';

import { Feedback } from '../../../types';
import './AdminFeedbackTable.css';

interface AdminFeedbackTableProps {
  items: Feedback[];
  total: number;
  skip: number;
  limit: number;
  loading: boolean;
  onPageChange: (skip: number) => void;
  onView?: (item: Feedback) => void;
  onDelete?: (id: number) => void;
  deletingId?: number | null;
}

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const ratingBadgeVariant = (rating: number): string => {
  if (rating <= 2) return 'danger';
  if (rating === 3) return 'warning';
  return 'success';
};

const RatingStars: React.FC<{ rating: number }> = memo(({ rating }) => (
  <span className="admin-feedback-table__stars" aria-label={`${rating} de 5 estrelas`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <i
        key={i}
        className={`bi ${i <= rating ? 'bi-star-fill' : 'bi-star'}`}
        aria-hidden="true"
      ></i>
    ))}
  </span>
));
RatingStars.displayName = 'RatingStars';

const AdminFeedbackTable: React.FC<AdminFeedbackTableProps> = memo(({
  items,
  total,
  skip,
  limit,
  loading,
  onPageChange,
  onView,
  onDelete,
  deletingId,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  const pages = useMemo(() => {
    const result: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) result.push(i);
    return result;
  }, [currentPage, totalPages]);

  const handlePage = useCallback(
    (page: number) => {
      const safe = Math.min(Math.max(1, page), totalPages);
      onPageChange((safe - 1) * limit);
    },
    [limit, onPageChange, totalPages],
  );

  const handleDelete = useCallback(
    (id: number) => {
      if (!onDelete) return;
      onDelete(id);
    },
    [onDelete],
  );

  const handleView = useCallback(
    (item: Feedback) => {
      if (!onView) return;
      onView(item);
    },
    [onView],
  );

  const hasActions = Boolean(onView) || Boolean(onDelete);

  if (!loading && items.length === 0) {
    return (
      <div className="admin-feedback-table__empty text-center py-5">
        <i className="bi bi-inbox fs-1 text-muted" aria-hidden="true"></i>
        <h6 className="text-muted mt-2 mb-1">Nenhum feedback encontrado</h6>
        <p className="text-muted small mb-0">Ajuste os filtros para ampliar a busca.</p>
      </div>
    );
  }

  return (
    <div className="admin-feedback-table-wrapper">
      {loading && (
        <div className="admin-feedback-table__overlay" aria-live="polite">
          <Spinner animation="border" variant="success" size="sm" />
          <span className="ms-2">Carregando...</span>
        </div>
      )}

      <div className="table-responsive admin-feedback-table">
        <Table hover className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th>Usuário</th>
              <th>Escopo</th>
              <th style={{ width: 130 }}>Nota</th>
              <th style={{ width: 110 }}>Anônimo</th>
              <th style={{ width: 160 }}>Data</th>
              {hasActions && (
                <th style={{ width: 110 }} className="text-end">Ações</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const isDeleting = deletingId === item.id;
              const userName = item.is_anonymous
                ? <em className="text-muted">Anônimo</em>
                : item.user?.name ?? <em className="text-muted">—</em>;
              const flowName = item.display_label
                ? item.display_label
                : item.workflow?.name
                  ? `${item.workflow.name} (v${item.workflow.version})`
                  : <em className="text-muted">Plataforma (geral)</em>;
              return (
                <tr key={item.id} className={isDeleting ? 'opacity-50' : ''}>
                  <td className="text-muted">{item.id}</td>
                  <td>{userName}</td>
                  <td>{flowName}</td>
                  <td>
                    <Badge bg={ratingBadgeVariant(item.rating)} className="me-2">
                      {item.rating}
                    </Badge>
                    <RatingStars rating={item.rating} />
                  </td>
                  <td>
                    {item.is_anonymous ? (
                      <Badge bg="secondary">Sim</Badge>
                    ) : (
                      <Badge bg="light" text="dark">Não</Badge>
                    )}
                  </td>
                  <td className="text-nowrap">{formatDate(item.created_at)}</td>
                  {hasActions && (
                    <td className="text-end admin-feedback-table__actions">
                      {onView && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleView(item)}
                          disabled={isDeleting}
                          aria-label={`Ver detalhes do feedback ${item.id}`}
                          title="Ver detalhes"
                        >
                          <i className="bi bi-eye" aria-hidden="true"></i>
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          disabled={isDeleting}
                          aria-label={`Excluir feedback ${item.id}`}
                          title="Excluir feedback"
                        >
                          {isDeleting ? (
                            <Spinner as="span" size="sm" animation="border" />
                          ) : (
                            <i className="bi bi-trash" aria-hidden="true"></i>
                          )}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
          <small className="text-muted">
            Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
            <span className="ms-2">
              · Mostrando {items.length} de {total} resultado{total === 1 ? '' : 's'}
            </span>
          </small>
          <Pagination className="mb-0" size="sm">
            <Pagination.First onClick={() => handlePage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePage(currentPage - 1)} disabled={currentPage === 1} />
            {pages[0] > 1 && <Pagination.Ellipsis disabled />}
            {pages.map((p) => (
              <Pagination.Item
                key={p}
                active={p === currentPage}
                onClick={() => handlePage(p)}
              >
                {p}
              </Pagination.Item>
            ))}
            {pages[pages.length - 1] < totalPages && <Pagination.Ellipsis disabled />}
            <Pagination.Next onClick={() => handlePage(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePage(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </div>
  );
});

AdminFeedbackTable.displayName = 'AdminFeedbackTable';

export default AdminFeedbackTable;
